import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { supabase } from '../supabase';
import authService from '../services/AuthService';
import './SignUp.css';
import '../styles/buttons.css';

const SignUp = () => {
  const navigate = useNavigate();
  
  // Form state
  const [formData, setFormData] = useState({
    email: '',
    username: '',
    password: '',
    confirmPassword: '',
    gender: '',
    day: '1',
    month: '1',
    year: '2000',
    country: '',
    agreeToTerms: false
  });

  // UI state
  const [isLoading, setIsLoading] = useState(false);
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [showConsentModal, setShowConsentModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [hasScrolledToBottom, setHasScrolledToBottom] = useState(false);
  const [hasAgreed, setHasAgreed] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Validation state
  const [passwordValidation, setPasswordValidation] = useState({
    length: false,
    uppercase: false,
    lowercase: false,
    number: false,
    special: false
  });
  const [fieldErrors, setFieldErrors] = useState({});
  const [touchedFields, setTouchedFields] = useState({});

  // Countries list
  const countries = [
    'United Kingdom',
    'Afghanistan', 'Albania', 'Algeria', 'Andorra', 'Angola', 'Antigua and Barbuda', 'Argentina', 'Armenia', 'Australia', 'Austria',
    'Azerbaijan', 'Bahamas', 'Bahrain', 'Bangladesh', 'Barbados', 'Belarus', 'Belgium', 'Belize', 'Benin', 'Bhutan',
    'Bolivia', 'Bosnia and Herzegovina', 'Botswana', 'Brazil', 'Brunei', 'Bulgaria', 'Burkina Faso', 'Burundi', 'Cabo Verde', 'Cambodia',
    'Cameroon', 'Canada', 'Central African Republic', 'Chad', 'Chile', 'China', 'Colombia', 'Comoros', 'Congo', 'Costa Rica',
    'Croatia', 'Cuba', 'Cyprus', 'Czech Republic', 'Democratic Republic of the Congo', 'Denmark', 'Djibouti', 'Dominica', 'Dominican Republic', 'Ecuador',
    'Egypt', 'El Salvador', 'Equatorial Guinea', 'Eritrea', 'Estonia', 'Eswatini', 'Ethiopia', 'Fiji', 'Finland', 'France',
    'Gabon', 'Gambia', 'Georgia', 'Germany', 'Ghana', 'Greece', 'Grenada', 'Guatemala', 'Guinea', 'Guinea-Bissau',
    'Guyana', 'Haiti', 'Honduras', 'Hungary', 'Iceland', 'India', 'Indonesia', 'Iran', 'Iraq', 'Ireland',
    'Israel', 'Italy', 'Ivory Coast', 'Jamaica', 'Japan', 'Jordan', 'Kazakhstan', 'Kenya', 'Kiribati', 'Kuwait',
    'Kyrgyzstan', 'Laos', 'Latvia', 'Lebanon', 'Lesotho', 'Liberia', 'Libya', 'Liechtenstein', 'Lithuania', 'Luxembourg',
    'Madagascar', 'Malawi', 'Malaysia', 'Maldives', 'Mali', 'Malta', 'Marshall Islands', 'Mauritania', 'Mauritius', 'Mexico',
    'Micronesia', 'Moldova', 'Monaco', 'Mongolia', 'Montenegro', 'Morocco', 'Mozambique', 'Myanmar', 'Namibia', 'Nauru',
    'Nepal', 'Netherlands', 'New Zealand', 'Nicaragua', 'Niger', 'Nigeria', 'North Korea', 'North Macedonia', 'Norway', 'Oman',
    'Pakistan', 'Palau', 'Palestine', 'Panama', 'Papua New Guinea', 'Paraguay', 'Peru', 'Philippines', 'Poland', 'Portugal',
    'Qatar', 'Romania', 'Russia', 'Rwanda', 'Saint Kitts and Nevis', 'Saint Lucia', 'Saint Vincent and the Grenadines', 'Samoa', 'San Marino', 'Sao Tome and Principe',
    'Saudi Arabia', 'Senegal', 'Serbia', 'Seychelles', 'Sierra Leone', 'Singapore', 'Slovakia', 'Slovenia', 'Solomon Islands', 'Somalia',
    'South Africa', 'South Korea', 'South Sudan', 'Spain', 'Sri Lanka', 'Sudan', 'Suriname', 'Sweden', 'Switzerland', 'Syria',
    'Taiwan', 'Tajikistan', 'Tanzania', 'Thailand', 'Timor-Leste', 'Togo', 'Tonga', 'Trinidad and Tobago', 'Tunisia', 'Turkey',
    'Turkmenistan', 'Tuvalu', 'Uganda', 'Ukraine', 'United Arab Emirates', 'United States', 'Uruguay', 'Uzbekistan', 'Vanuatu',
    'Vatican City', 'Venezuela', 'Vietnam', 'Yemen', 'Zambia', 'Zimbabwe'
  ];

  // Generate days, months, years
  const days = Array.from({ length: 31 }, (_, i) => i + 1);
  const months = [
    { value: '1', label: 'January' }, { value: '2', label: 'February' }, { value: '3', label: 'March' },
    { value: '4', label: 'April' }, { value: '5', label: 'May' }, { value: '6', label: 'June' },
    { value: '7', label: 'July' }, { value: '8', label: 'August' }, { value: '9', label: 'September' },
    { value: '10', label: 'October' }, { value: '11', label: 'November' }, { value: '12', label: 'December' }
  ];
  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: currentYear - 1900 + 1 }, (_, i) => currentYear - i);

  // Password validation
  useEffect(() => {
    const password = formData.password;
    setPasswordValidation({
      length: password.length >= 8,
      uppercase: /[A-Z]/.test(password),
      lowercase: /[a-z]/.test(password),
      number: /\d/.test(password),
      special: (password.match(/[!@#$%^&*(),.?":{}|<>]/g) || []).length >= 2
    });
  }, [formData.password]);


  // Real-time validation
  const validateField = (name, value) => {
    const errors = { ...fieldErrors };
    
    switch (name) {
      case 'email':
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!value) {
          errors.email = 'Email is required';
        } else if (!emailRegex.test(value)) {
          errors.email = 'Please enter a valid email address';
        } else {
          delete errors.email;
        }
        break;
        
      case 'username':
        if (!value) {
          errors.username = 'Username is required';
        } else if (value.length < 3) {
          errors.username = 'Username must be at least 3 characters';
        } else if (/\s/.test(value)) {
          errors.username = 'Username cannot contain spaces';
        } else if (!/^[a-zA-Z0-9_]+$/.test(value)) {
          errors.username = 'Username can only contain letters, numbers, and underscores';
        } else {
          delete errors.username;
        }
        break;
        
      case 'password':
        if (!value) {
          errors.password = 'Password is required';
        } else if (!passwordValidation.length || !passwordValidation.uppercase || 
                   !passwordValidation.lowercase || !passwordValidation.number || 
                   !passwordValidation.special) {
          errors.password = 'Password does not meet requirements';
        } else {
          delete errors.password;
        }
        break;
        
      case 'confirmPassword':
        if (!value) {
          errors.confirmPassword = 'Please confirm your password';
        } else if (value !== formData.password) {
          errors.confirmPassword = 'Passwords do not match';
        } else {
          delete errors.confirmPassword;
        }
        break;
        
      case 'gender':
        if (!value) {
          errors.gender = 'Please select a gender';
        } else {
          delete errors.gender;
        }
        break;
        
      case 'country':
        if (!value) {
          errors.country = 'Please select a country';
        } else {
          delete errors.country;
        }
        break;
        
      default:
        break;
    }
    
    setFieldErrors(errors);
  };

  const handleInputChange = (name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));
    setErrorMessage('');
    // Clear the error and touched state when user starts typing
    if (fieldErrors[name]) {
      setFieldErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
    if (touchedFields[name]) {
      setTouchedFields(prev => {
        const newTouched = { ...prev };
        delete newTouched[name];
        return newTouched;
      });
    }
  };

  const handleBlur = (name) => {
    // Mark field as touched when user clicks away without typing
    if (!formData[name] || formData[name] === '') {
      setTouchedFields(prev => ({ ...prev, [name]: true }));
      validateField(name, formData[name]);
    }
  };

  const checkUniqueEmail = async (email) => {
    const { data, error } = await supabase
      .from('user_profiles')
      .select('email')
      .eq('email', email)
      .maybeSingle();
    
    // If error is PGRST116 (no rows found), that means email is unique
    if (error && error.code === 'PGRST116') {
      return true; // Email is unique
    }
    
    // If there's another error, return false to be safe
    if (error) {
      console.error('Error checking email uniqueness:', error);
      return false;
    }
    
    // If data exists, email is not unique
    return !data; // Return true if email is unique (no data found)
  };

  const checkUniqueUsername = async (username) => {
    const { data, error } = await supabase
      .from('user_profiles')
      .select('username')
      .eq('username', username)
      .maybeSingle();
    
    // If error is PGRST116 (no rows found), that means username is unique
    if (error && error.code === 'PGRST116') {
      return true; // Username is unique
    }
    
    // If there's another error, return false to be safe
    if (error) {
      console.error('Error checking username uniqueness:', error);
      return false;
    }
    
    // If data exists, username is not unique
    return !data; // Return true if username is unique (no data found)
  };

  const validateAge = () => {
    const birthDate = new Date(
      parseInt(formData.year),
      parseInt(formData.month) - 1,
      parseInt(formData.day)
    );
    const today = new Date();
    const age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      return age - 1;
    }
    return age;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    // Show consent modal, then proceed with signup
    setShowConsentModal(true);
  };

  const handleConsent = () => {
    setShowConsentModal(false);
    // Proceed with signup - consent info will be stored in user_metadata
    proceedWithSignup(true);
  };

  const handleNoConsent = () => {
    setShowConsentModal(false);
    // Proceed with signup even without consent - user can consent later
    proceedWithSignup(false);
  };

  const proceedWithSignup = async (hasConsented = false) => {
    setIsLoading(true);
    setErrorMessage('');
    setSuccessMessage('');

    try {
      // Validate all fields and collect errors
      const validationErrors = {};
      const requiredFields = ['email', 'username', 'password', 'confirmPassword', 'gender', 'country'];
      
      requiredFields.forEach(field => {
        const value = formData[field];
        const errors = { ...fieldErrors };
        
        // Validate the field
        switch (field) {
          case 'email':
            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            if (!value) {
              validationErrors.email = 'Email is required';
            } else if (!emailRegex.test(value)) {
              validationErrors.email = 'Please enter a valid email address';
            }
            break;
            
          case 'username':
            if (!value) {
              validationErrors.username = 'Username is required';
            } else if (value.length < 3) {
              validationErrors.username = 'Username must be at least 3 characters';
            } else if (/\s/.test(value)) {
              validationErrors.username = 'Username cannot contain spaces';
            } else if (!/^[a-zA-Z0-9_]+$/.test(value)) {
              validationErrors.username = 'Username can only contain letters, numbers, and underscores';
            }
            break;
            
          case 'password':
            if (!value) {
              validationErrors.password = 'Password is required';
            } else if (!passwordValidation.length || !passwordValidation.uppercase || 
                       !passwordValidation.lowercase || !passwordValidation.number || 
                       !passwordValidation.special) {
              validationErrors.password = 'Password does not meet requirements';
            }
            break;
            
          case 'confirmPassword':
            if (!value) {
              validationErrors.confirmPassword = 'Please confirm your password';
            } else if (value !== formData.password) {
              validationErrors.confirmPassword = 'Passwords do not match';
            }
            break;
            
          case 'gender':
            if (!value) {
              validationErrors.gender = 'Please select a gender';
            }
            break;
            
          case 'country':
            if (!value) {
              validationErrors.country = 'Please select a country';
            }
            break;
        }
      });

      // Update field errors and touched fields
      setFieldErrors(validationErrors);
      
      // If there are validation errors, show them and stop
      if (Object.keys(validationErrors).length > 0) {
        setTouchedFields(prev => {
          const newTouched = { ...prev };
          Object.keys(validationErrors).forEach(field => {
            newTouched[field] = true;
          });
          return newTouched;
        });
        
        // Show the first validation error message in the error banner
        const firstErrorField = Object.keys(validationErrors)[0];
        const firstErrorMessage = validationErrors[firstErrorField];
        setErrorMessage(firstErrorMessage);
        setIsLoading(false);
        return;
      }

      // Check age requirement
      const age = validateAge();
      if (age < 18) {
        setErrorMessage('You must be at least 18 years old to create an account');
        setIsLoading(false);
        return;
      }

      // Check terms agreement
      if (!formData.agreeToTerms) {
        setErrorMessage('You must agree to the terms and conditions');
        setIsLoading(false);
        return;
      }

      // Check unique email and username
      const isEmailUnique = await checkUniqueEmail(formData.email);
      const isUsernameUnique = await checkUniqueUsername(formData.username);

      if (!isEmailUnique) {
        setErrorMessage('An account with this email already exists');
        setIsLoading(false);
        return;
      }

      if (!isUsernameUnique) {
        setErrorMessage('This username is already taken');
        setIsLoading(false);
        return;
      }

      // Construct date of birth from day, month, year
      const dob = new Date(
        parseInt(formData.year),
        parseInt(formData.month) - 1,
        parseInt(formData.day)
      ).toISOString();

      // Consent text version and snapshot
      const consentText = "I explicitly consent to the processing of my health and biometric data, including wearable data and laboratory biomarkers (e.g. hormone levels), to provide personalised health insights and reports.";
      const consentTextVersion = "v1.0-2026-01-12";
      
      // Send signup email using our custom service with password, username, gender, country, dob, and consent info
      const result = await authService.sendSignupEmail(
        formData.email, 
        formData.password, 
        formData.username,
        formData.gender,
        formData.country,
        dob,
        hasConsented ? {
          consent_type: 'health_biometric_processing',
          consent_text_version: consentTextVersion,
          consent_text_snapshot: consentText
        } : null
      );
      
      if (!result.success) {
        // Use the error message from result (which may be more specific) or fall back to getErrorMessage
        const errorMsg = result.message || result.error || 'Unable to create your account. Please try again.';
        setErrorMessage(authService.getErrorMessage('signup', errorMsg));
        setIsLoading(false);
        return;
      }

      // Show success message
      setSuccessMessage('Account creation email sent! Please check your email (including your Junk/Spam folder) and click the verification link to complete your registration.');

    } catch (error) {
      console.error('Sign up error:', error);
      setErrorMessage('Something went wrong. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="signup-container">
      <div className="signup-card">
        {/* Logo and Brand */}
        <div className="logo">
          <svg
            version="1.1"
            id="svg1"
            width="400"
            height="300"
            viewBox="0 0 400 300"
            xmlns="http://www.w3.org/2000/svg"
            className="logo-image"
          >
            <defs id="defs1" />
            <g id="g1">
              <path
                style={{ fill: '#000000' }}
                  d="m 36.324107,269.8424 c -6.327,-2.00097 -9.95387,-4.21514 -13.47665,-8.22737 -4.5408,-5.17168 -6.02335,-9.26867 -6.02335,-16.64535 0,-14.52134 10.98445,-25.4399 25.80718,-25.65238 7.71011,-0.11053 13.58888,1.68364 17.90151,5.46343 l 3.79131,3.32288 -3.71957,3.77171 -3.71957,3.7717 -2.28043,-2.13629 c -6.97288,-6.53214 -18.08848,-5.24252 -23.53043,2.72998 -3.10435,4.54791 -3.10278,12.4393 0.003,17.04856 4.99831,7.41699 13.11502,9.15267 21.33002,4.56122 l 4.76545,-2.66346 3.32557,3.22326 c 1.82906,1.77279 3.31595,3.47249 3.30421,3.7771 -0.0539,1.39801 -8.02024,5.9887 -12.91458,7.44216 -6.36767,1.891 -9.13014,1.93137 -14.56407,0.21285 z m 255.500003,0.50962 c -7.45121,-1.60362 -12.72559,-5.81647 -15.45227,-12.34233 -1.1914,-2.85142 -1.54773,-7.62548 -1.54773,-20.73635 v -17.03212 l 5.75,-0.002 5.75,-0.002 -0.3243,11.75197 c -0.44922,16.27866 0.34584,21.68491 3.74918,25.49391 2.29669,2.57045 3.50541,3.09138 7.17295,3.09138 10.22129,0 11.90217,-3.50959 11.90217,-24.85112 v -15.58202 l 5.71912,0.29991 5.71912,0.2999 -0.0433,16 c -0.0447,16.51186 -0.886,21.45223 -4.54073,26.66417 -3.71783,5.30195 -15.52275,8.73968 -23.85421,6.94663 z m 48.23015,-0.60187 c -5.50871,-1.5451 -10.80639,-3.95952 -11.5234,-5.25182 -0.23104,-0.41641 0.43557,-2.67677 1.48137,-5.02302 l 1.90144,-4.26591 4.20522,2.4595 c 8.74506,5.11471 20.67798,5.38422 22.22753,0.50203 1.20386,-3.79301 -2.07789,-6.12117 -12.4767,-8.85128 -12.53052,-3.28979 -16.88472,-7.78575 -15.74798,-16.26073 1.10763,-8.25804 7.67626,-13.02049 18.83173,-13.65355 6.77543,-0.3845 17.12588,1.82178 18.42759,3.92799 0.3623,0.58622 0.0952,3.07852 -0.59351,5.53844 -0.97989,3.49982 -1.60145,4.32324 -2.85784,3.78594 -9.54281,-4.081 -17.68903,-4.6393 -20.60928,-1.41246 -1.42633,1.57608 -1.59416,2.48578 -0.7859,4.25973 0.79922,1.75409 3.36849,2.97212 11.55437,5.47763 8.3933,2.56901 11.0544,3.8492 13.13091,6.317 3.31504,3.9397 3.56671,10.85077 0.57389,15.75961 -3.91809,6.42647 -17.23236,9.63794 -27.73944,6.6909 z M 72.209037,268.6805 c 0.33829,-0.88157 0.61507,-12.14105 0.61507,-25.02107 v -23.41821 h 18.999998 19.000005 l 10e-4,4.75 0.001,4.75 -13.001425,-0.32947 -13.000578,-0.32947 v 5.57947 5.57947 h 11.499998 11.500005 l 10e-4,4.75 0.001,4.75 -11.440145,-0.33455 -11.440128,-0.33455 -0.64789,3.45356 c -0.35634,1.89946 -0.49637,4.525 -0.31118,5.83455 0.33544,2.37193 0.39001,2.38144 14.336718,2.50023 l 14.000005,0.11924 v 4.38076 4.38076 l -20.365075,0.27107 c -18.340008,0.24412 -20.303918,0.11169 -19.749998,-1.33179 z m 60.385783,-18.60093 -0.27071,-20.16166 -7.75,0.22695 -7.75,0.22695 v -5.06529 -5.0653 h 22 22 v 5 5 h -8 -8 v 20 20 h -5.97929 -5.9793 z m 26.15197,17.91165 c 1.20858,-3.11425 17.39164,-39.38801 19.64893,-44.04239 l 1.83924,-3.79238 5.98049,0.29238 5.9805,0.29239 6.84096,15.5 c 3.76253,8.525 8.62887,19.23785 10.81408,23.80634 2.18522,4.56849 3.97312,8.73099 3.97312,9.25 0,0.51901 -2.73871,0.94366 -6.08603,0.94366 h -6.08603 l -2.11244,-5 -2.11244,-5 h -11.7011 -11.7011 l -1.79797,5 -1.79798,5 h -6.2777 c -6.10227,0 -6.25331,-0.0629 -5.40453,-2.25 z m 34.07732,-17.68605 c 0,-1.70625 -6.30006,-17.06395 -7,-17.06395 -0.69994,0 -7,15.3577 -7,17.06395 0,0.51483 3.15,0.93605 7,0.93605 3.85,0 7,-0.42122 7,-0.93605 z m 28,-5.06395 v -25 l 14.75,0.022 c 12.23903,0.0182 15.4507,0.34017 18.86609,1.89116 11.34542,5.15216 13.70823,19.29905 4.80076,28.74372 -1.87927,1.99261 -3.41685,3.80966 -3.41685,4.03791 0,0.22824 1.81069,3.08134 4.02375,6.34022 6.19713,9.12568 6.22127,8.96503 -1.34672,8.96503 h -6.53694 l -4.14009,-7.50004 -4.14009,-7.5 h -5.42996 -5.42995 v 7.5 7.5 h -6 -6 z m 28.97456,-0.97456 c 7.14796,-7.14796 0.75746,-15.5241 -11.31039,-14.82472 l -5.16417,0.29928 -0.29044,8.25 -0.29044,8.25 h 7.54044 c 6.17473,0 7.89807,-0.35763 9.515,-1.97456 z m -89.70462,-69.52678 c -4.25178,-2.12708 -8.81821,-5.44648 -11.63435,-8.45716 -5.52049,-5.90185 -5.89233,-7.54285 -1.51059,-6.6665 1.71875,0.34375 6.81098,0.625 11.31607,0.625 h 8.19107 l 4.18393,8.25157 c 2.30116,4.53836 4.18393,8.58836 4.18393,9 0,2.0263 -8.26429,0.48178 -14.73006,-2.75291 z m 45.83551,-2.97307 c -16.63086,-4.02108 -32.79578,-16.81084 -41.10892,-32.52559 l -4.49653,-8.5 -7,-0.653 c -12.05307,-1.12438 -20.92832,-3.67791 -27.46303,-7.9015 -16.0391,-10.36657 -21.17255,-11.8546 -45.036973,-13.05485 -26.35497,-1.32552 -47.36244,-3.50959 -50.78617,-5.28007 -6.60701,-3.41662 -6.73956,-15.303122 -0.21609,-19.377102 2.62531,-1.63953 15.52887,-2.91433 42.50226,-4.19899 12.1,-0.57628 24.166748,-1.31604 26.814998,-1.64392 2.648255,-0.32787 34.598255,-0.036 71.000005,0.64869 97.4079,1.83206 99.60639,1.45834 136.90518,-23.27278 9.22026,-6.11353 16.59833,-11.28131 16.3957,-11.48394 -0.20263,-0.20263 -2.7866,0.55068 -5.74215,1.67402 -2.95555,1.12334 -5.48623,2.04266 -5.62373,2.04294 -0.1375,2.7e-4 -0.25,-3.3271 -0.25,-7.39416 0,-11.00385 2.94321,-19.51558 8.8768,-25.67157 6.34698,-6.5848802 7.58027,-6.4798502 6.75112,0.57498 -0.75283,6.40537 0.72667,10.8532 5.36878,16.14027 3.13225,3.56744 3.83912,7.11516 1.81997,9.13431 -1.98082,1.98082 -0.37339,2.30996 5.10517,1.04535 6.80005,-1.56964 12.40111,-0.13265 20.82656,5.3432 7.45509,4.8452 12.39103,6.66432 20.33599,7.49473 3.25359,0.34007 5.91561,0.92576 5.91561,1.30153 0,1.44445 -4.73676,5.03048 -9.51246,7.20153 -6.5395,2.9729 -18.4859,2.95917 -30.48754,-0.0351 -10.71473,-2.67315 -19.33613,-2.77216 -24.0156,-0.27579 -3.60756,1.92452 -8.5761,8.13069 -24.9844,31.2078 -17.32484,24.366172 -34.63757,39.564002 -55.71653,48.910302 -8.60616,3.81597 -24.86904,9.02312 -28.18071,9.02312 -1.05848,0 -2.813,-2.31079 -4.77418,-6.28785 -4.1414,-8.39833 -7.59602,-12.30724 -13.53799,-15.31826 -6.25499,-3.16965 -8.61817,-2.57268 -3.24446,0.8196 5.30637,3.34976 9.51613,8.24718 13.47688,15.6783 3.35193,6.28886 10.13186,13.28597 18.25295,18.8376 2.59822,1.77617 4.72404,3.61644 4.72404,4.08949 0,3.114 -13.05042,4.08687 -22.89455,1.70671 z m -72.69298,-55.13037 c 2.21835,-0.85126 0.94753,-2.39522 -1.97149,-2.39522 -2.5675,0 -3.32067,1.47837 -1.19098,2.33772 0.6875,0.27741 1.3257,0.53991 1.41821,0.58333 0.0925,0.0434 0.87743,-0.1932 1.74426,-0.52583 z m 4.58753,-3.19522 c 0,-2.78034 -8.97866,-4.66158 -10.5,-2.2 -0.36387,0.58876 0.74278,1.0063 2.69098,1.01531 1.81996,0.008 3.98402,0.45153 4.80902,0.98469 1.83718,1.18727 3,1.2648 3,0.2 z m 7.5,40.77676 c -20.49142,-3.15205 -43.06844,-10.90438 -64.500003,-22.14752 -11.9703,-6.2797 -29.6722,-17.52442 -31.9965,-20.32502 -1.21903,-1.46884 -0.8429,-1.55583 4.07679,-0.94286 4.26009,0.53079 6.59651,1.58365 10.91971,4.92077 26.875728,20.74564 63.732883,35.0798 95.477093,37.13211 6.90674,0.44653 10.90823,1.14512 11.64513,2.03303 1.63543,1.97057 -10.52066,1.65245 -25.62222,-0.67051 z m -27.085,-0.97287 C 89.788725,146.81433 52.465407,128.11804 38.324107,113.21346 l -4,-4.2159 4.75808,0.4999 c 3.11456,0.32723 5.8779,1.41189 8,3.14014 21.55823,17.55709 52.487258,32.85601 82.144663,40.63251 l 7.09726,1.86099 -5,-0.0292 c -2.75,-0.0161 -8.63825,-0.85161 -13.085,-1.85674 z m 26.4133,-4.04532 c -22.50993,-3.96533 -51.656535,-15.96472 -72.328303,-29.7769 l -10,-6.68167 5.83948,-0.30257 c 5.47888,-0.28389 6.15816,-0.0573 11,3.66876 21.849403,16.81448 53.424543,29.354 79.854513,31.71284 5.07182,0.45266 7.87279,1.15262 8.21862,2.05383 0.7561,1.97036 -9.27089,1.67099 -22.58431,-0.67429 z m 5.6717,-4.54964 C 126.75958,140.99146 97.687735,128.4645 79.824107,114.27193 l -2.5,-1.98624 5.89701,-0.0222 c 5.297268,-0.02 6.340128,0.34087 10.254098,3.54807 16.639015,13.63442 40.036515,23.29664 61.117725,25.23915 6.22918,0.57398 7.74579,1.07034 8.98466,2.94054 1.43134,2.16076 1.37654,2.24616 -1.38152,2.15314 -1.57958,-0.0533 -6.92197,-0.72566 -11.87197,-1.49421 z m -1,-6.12319 c -6.18518,-0.85478 -18.45181,-4.24227 -26.2455,-7.24784 -8.17282,-3.15177 -24.095095,-12.4588 -27.754505,-16.2233 l -2,-2.05744 5.5,0.59233 c 3.633945,0.39137 8.214265,1.99996 13.500005,4.74115 4.4,2.28184 10.925,5.67407 14.5,7.53827 6.37404,3.32377 21.38192,7.30667 27.6933,7.34945 2.93569,0.0199 6.3067,2.82296 6.3067,5.24415 0,0.78795 -6.01657,0.82103 -11.5,0.0632 z"
                id="path1"
              />
            </g>
          </svg>
        </div>

        {/* Header */}
        <div className="signup-header">
          <h2>Create your account</h2>
          <p className="signup-subtitle">Join Cetarus and start your biohacking journey</p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="signup-form">
          {/* Email */}
          <div className="form-group">
            <label htmlFor="email">Email Address</label>
            <input
              type="email"
              id="email"
              value={formData.email}
              onChange={(e) => handleInputChange('email', e.target.value)}
              onBlur={() => handleBlur('email')}
              placeholder={touchedFields.email && fieldErrors.email ? fieldErrors.email : "Enter your email address"}
              className={`form-input ${fieldErrors.email && touchedFields.email ? 'error' : ''}`}
            />
          </div>

          {/* Username */}
          <div className="form-group">
            <label htmlFor="username">Username</label>
            <input
              type="text"
              id="username"
              value={formData.username}
              onChange={(e) => handleInputChange('username', e.target.value)}
              onBlur={() => handleBlur('username')}
              placeholder={touchedFields.username && fieldErrors.username ? fieldErrors.username : "Enter your username"}
              className={`form-input ${fieldErrors.username && touchedFields.username ? 'error' : ''}`}
            />
          </div>

          {/* Password */}
          <div className="form-group">
            <label htmlFor="password">Password</label>
            <div className="password-input-wrapper">
              <input
                type={showPassword ? "text" : "password"}
                id="password"
                value={formData.password}
                onChange={(e) => handleInputChange('password', e.target.value)}
                onBlur={() => handleBlur('password')}
                placeholder={touchedFields.password && fieldErrors.password ? fieldErrors.password : "Enter a password"}
                className={`form-input ${fieldErrors.password && touchedFields.password ? 'error' : ''}`}
              />
              <button
                type="button"
                className="password-toggle"
                onClick={() => setShowPassword(!showPassword)}
                aria-label={showPassword ? "Hide password" : "Show password"}
              >
                {showPassword ? (
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"></path>
                    <line x1="1" y1="1" x2="23" y2="23"></line>
                  </svg>
                ) : (
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                    <circle cx="12" cy="12" r="3"></circle>
                  </svg>
                )}
              </button>
            </div>
            
            {/* Password validation */}
            <div className="password-validation">
              <div className={`validation-item ${passwordValidation.length ? 'valid' : ''}`}>
                At least 8 characters
              </div>
              <div className={`validation-item ${passwordValidation.uppercase ? 'valid' : ''}`}>
                One uppercase letter
              </div>
              <div className={`validation-item ${passwordValidation.lowercase ? 'valid' : ''}`}>
                One lowercase letter
              </div>
              <div className={`validation-item ${passwordValidation.number ? 'valid' : ''}`}>
                One number
              </div>
              <div className={`validation-item ${passwordValidation.special ? 'valid' : ''}`}>
                Two special characters
              </div>
            </div>
          </div>

          {/* Confirm Password */}
          <div className="form-group">
            <label htmlFor="confirmPassword">Confirm Password</label>
            <div className="password-input-wrapper">
              <input
                type={showConfirmPassword ? "text" : "password"}
                id="confirmPassword"
                value={formData.confirmPassword}
                onChange={(e) => handleInputChange('confirmPassword', e.target.value)}
                onBlur={() => handleBlur('confirmPassword')}
                placeholder={touchedFields.confirmPassword && fieldErrors.confirmPassword ? fieldErrors.confirmPassword : "Confirm your password"}
                className={`form-input ${fieldErrors.confirmPassword && touchedFields.confirmPassword ? 'error' : ''}`}
              />
              <button
                type="button"
                className="password-toggle"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                aria-label={showConfirmPassword ? "Hide password" : "Show password"}
              >
                {showConfirmPassword ? (
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"></path>
                    <line x1="1" y1="1" x2="23" y2="23"></line>
                  </svg>
                ) : (
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                    <circle cx="12" cy="12" r="3"></circle>
                  </svg>
                )}
              </button>
            </div>
          </div>

          {/* Gender */}
          <div className="form-group">
            <label>Gender</label>
            <div className="gender-buttons">
              {['Male', 'Female', 'Other'].map(gender => (
                <button
                  key={gender}
                  type="button"
                  className={`btn btn--secondary btn--md btn--rounded ${formData.gender === gender ? 'btn--primary' : ''}`}
                  onClick={() => handleInputChange('gender', gender)}
                >
                  {gender}
                </button>
              ))}
            </div>
          </div>

          {/* Date of Birth */}
          <div className="form-group">
            <label>Date of Birth</label>
            <div className="dob-selectors">
              <select
                value={formData.day}
                onChange={(e) => handleInputChange('day', e.target.value)}
                className="dob-select"
              >
                {days.map(day => (
                  <option key={day} value={day}>{day}</option>
                ))}
              </select>
              <select
                value={formData.month}
                onChange={(e) => handleInputChange('month', e.target.value)}
                className="dob-select"
              >
                {months.map(month => (
                  <option key={month.value} value={month.value}>{month.label}</option>
                ))}
              </select>
              <select
                value={formData.year}
                onChange={(e) => handleInputChange('year', e.target.value)}
                className="dob-select"
              >
                {years.map(year => (
                  <option key={year} value={year}>{year}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Country */}
          <div className="form-group">
            <label htmlFor="country">Country of Residence</label>
            <select
              id="country"
              value={formData.country}
              onChange={(e) => handleInputChange('country', e.target.value)}
              onBlur={() => handleBlur('country')}
              className={`form-input ${fieldErrors.country && touchedFields.country ? 'error' : ''}`}
            >
              <option value="">{touchedFields.country && fieldErrors.country ? fieldErrors.country : "Select a country"}</option>
              {countries.map(country => (
                <option key={country} value={country}>{country}</option>
              ))}
            </select>
          </div>

          {/* Terms and Conditions */}
          <div className="form-group">
            <div className="terms-text">
              Please read and accept our{' '}
              <button
                type="button"
                className="terms-link"
                onClick={() => {
                  setShowTermsModal(true);
                  // If already agreed, show the agreed state
                  if (formData.agreeToTerms) {
                    setHasScrolledToBottom(true);
                    setHasAgreed(true);
                  } else {
                    setHasScrolledToBottom(false);
                    setHasAgreed(false);
                  }
                }}
              >
                terms and conditions
              </button>
            </div>
          </div>

          {/* Error and Success Messages */}
          {errorMessage && <div className="error-banner">{errorMessage}</div>}
          {successMessage && <div className="success-banner">{successMessage}</div>}

          {/* Submit Button */}
          <button
            type="submit"
            disabled={isLoading}
            className="btn btn--primary btn--lg btn--rounded"
          >
            {isLoading ? 'Creating Account...' : 'Sign Up'}
          </button>
        </form>

        {/* Sign In Link */}
        <div className="signup-footer">
          <p>
            Have an account?{' '}
            <Link to="/signin" className="link">
              Sign in
            </Link>
          </p>
        </div>
      </div>

      {/* Health Data Consent Modal */}
      {showConsentModal && (
        <div className="modal-overlay" onClick={(e) => {
          // Don't close on overlay click - user must explicitly choose
          e.stopPropagation();
        }}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '500px' }}>
            <div className="modal-header">
              <h3>Health Data Consent</h3>
            </div>
            <div className="modal-body" style={{ padding: '24px' }}>
              <p style={{ marginBottom: '20px', fontSize: '14px', lineHeight: '1.6', color: 'var(--color-text-primary)' }}>
                I explicitly consent to the processing of my health and biometric data, including wearable data and laboratory biomarkers (e.g. hormone levels), to provide personalised health insights and reports.
              </p>
              <p style={{ marginBottom: '20px', fontSize: '14px', lineHeight: '1.6', color: 'var(--color-text-secondary)', fontStyle: 'italic' }}>
                You can choose not to consent and still use the platform. However, protocol creation, health tracking, and insights will be disabled until consent is given.
              </p>
              <p style={{ marginBottom: '20px', fontSize: '14px', lineHeight: '1.6', color: 'var(--color-text-secondary)', fontStyle: 'italic' }}>
                You can withdraw this consent at any time in account settings.
              </p>
              <div style={{ display: 'flex', gap: '12px', marginTop: '24px' }}>
                <button
                  type="button"
                  className="btn btn--secondary btn--md btn--rounded"
                  onClick={handleNoConsent}
                  style={{ flex: 1 }}
                >
                  I don't consent
                </button>
                <button
                  type="button"
                  className="btn btn--primary btn--md btn--rounded"
                  onClick={handleConsent}
                  style={{ flex: 1 }}
                >
                  I consent
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Terms and Conditions Modal */}
      {showTermsModal && (
        <div className="modal-overlay" onClick={() => setShowTermsModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Terms and Conditions</h3>
              <button
                className="modal-close"
                onClick={() => setShowTermsModal(false)}
              >
                ×
              </button>
            </div>
            <div 
              className="modal-body"
              onScroll={(e) => {
                const element = e.target;
                const isAtBottom = element.scrollHeight - element.scrollTop <= element.clientHeight + 10; // 10px threshold
                if (isAtBottom) {
                  setHasScrolledToBottom(true);
                }
              }}
            >
              <p>
                These Terms of Service ("Terms") govern your access to and use of the Cetarus website, applications, and services (collectively, the "Service"). By creating an account, accessing, or using the Service, you agree to be bound by these Terms.
              </p>
              
              <p>
                If you do not agree to these Terms, you may not use the Service.
              </p>
              
              <h4>1. What the Service Is (and Is Not)</h4>
              <p>
                Cetarus is a self-experimentation, tracking, and social platform that enables users to:
              </p>
              <ul>
                <li>Research and organize lifestyle interventions, supplements, habits, and other "biohacks"</li>
                <li>Create and share user-generated protocols</li>
                <li>Track self-reported data, wearable data, and biomarker data</li>
                <li>Analyze and visualize logged data over time</li>
                <li>Share experiences, observations, and results socially</li>
              </ul>
              <p>
                <strong>The Service is not a medical service.<br />
                We do not provide medical advice, diagnosis, treatment, or clinical recommendations.</strong>
              </p>
              <p>
                All content, insights, scores, reports, analytics, and visualizations are:
              </p>
              <ul>
                <li>For informational and self-experimentation purposes only</li>
                <li>Not a substitute for professional medical advice</li>
                <li>Not intended to prevent, diagnose, or treat any disease</li>
              </ul>
              <p>
                Always consult a qualified healthcare professional before making health-related decisions.
              </p>
              
              <h4>2. Eligibility</h4>
              <p>
                You must be at least 18 years old to use the Service.
              </p>
              <p>
                By using the Service, you represent that:
              </p>
              <ul>
                <li>You are legally able to enter into these Terms</li>
                <li>You are responsible for complying with applicable laws in your jurisdiction</li>
              </ul>
              
              <h4>3. User Accounts</h4>
              <p>
                To access certain features, you must create an account.
              </p>
              <p>
                You are responsible for:
              </p>
              <ul>
                <li>Maintaining the confidentiality of your account credentials</li>
                <li>All activity that occurs under your account</li>
                <li>Ensuring information you provide is accurate and up to date</li>
              </ul>
              <p>
                We may suspend or terminate accounts that violate these Terms.
              </p>
              
              <h4>4. User-Generated Content</h4>
              <p>
                You retain ownership of content you create or upload, including:
              </p>
              <ul>
                <li>Protocols</li>
                <li>Biohack combinations</li>
                <li>Posts, comments, messages</li>
                <li>Images, reports, and data visualizations</li>
              </ul>
              <p>
                By submitting content, you grant Cetarus a non-exclusive, worldwide, royalty-free license to use, display, reproduce, and distribute that content solely for operating and improving the Service.
              </p>
              <p>
                You agree not to post content that:
              </p>
              <ul>
                <li>Is false, misleading, or fraudulent</li>
                <li>Constitutes medical advice presented as fact</li>
                <li>Is unlawful, abusive, defamatory, or infringing</li>
                <li>Violates the rights of others</li>
              </ul>
              <p>
                We reserve the right to remove content at our discretion.
              </p>
              
              <h4>5. Protocols, Scores, and Community Voting</h4>
              <p>
                Protocols, protocol cards, scores, and votes are user-generated and opinion-based.
              </p>
              <ul>
                <li>Scores reflect community sentiment, not effectiveness or safety</li>
                <li>We do not validate, endorse, or verify protocols</li>
                <li>Use of any protocol is at your own risk</li>
              </ul>
              
              <h4>6. Wearables and Third-Party Data Sources</h4>
              <p>
                The Service may allow you to connect third-party devices or services (e.g. wearable devices or biomarker providers).
              </p>
              <p>
                By connecting a third-party service, you authorize us to:
              </p>
              <ul>
                <li>Access data you explicitly approve</li>
                <li>Store and process that data to provide the Service</li>
              </ul>
              <p>
                We do not control third-party services and are not responsible for:
              </p>
              <ul>
                <li>Their accuracy</li>
                <li>Their availability</li>
                <li>Their data practices</li>
              </ul>
              <p>
                You may disconnect third-party integrations at any time.
              </p>
              
              <h4>7. Reports, Analytics, and Insights</h4>
              <p>
                Reports and analytics generated by the Service are:
              </p>
              <ul>
                <li>Automatically or user-generated summaries of your data</li>
                <li>Intended for reflection and comparison only</li>
                <li>Not medical conclusions or recommendations</li>
              </ul>
              <p>
                We make no guarantees regarding accuracy, completeness, or outcomes.
              </p>
              
              <h4>8. Commerce and Purchases</h4>
              <p>
                The Service may link to or facilitate purchases of third-party products.
              </p>
              <ul>
                <li>We do not manufacture or control these products</li>
                <li>We are not responsible for product quality, safety, or outcomes</li>
                <li>Any disputes related to purchases are between you and the seller</li>
              </ul>
              
              <h4>9. Social Features and Messaging</h4>
              <p>
                You are responsible for your interactions with other users.
              </p>
              <p>
                We do not:
              </p>
              <ul>
                <li>Screen private messages</li>
                <li>Guarantee the behavior or intent of other users</li>
              </ul>
              <p>
                We reserve the right to investigate and act on reports of abuse or misuse.
              </p>
              
              <h4>10. Data, Privacy, and Health Information</h4>
              <p>
                Your use of the Service is subject to our Privacy Policy, which explains:
              </p>
              <ul>
                <li>What data we collect</li>
                <li>How we use and store data</li>
                <li>How you can control or delete your data</li>
              </ul>
              <p>
                By using the Service, you acknowledge that:
              </p>
              <ul>
                <li>You are voluntarily providing health-related information</li>
                <li>You understand the risks of sharing data socially</li>
              </ul>
              
              <h4>11. Account Termination</h4>
              <p>
                You may stop using the Service at any time.
              </p>
              <p>
                We may suspend or terminate your account if:
              </p>
              <ul>
                <li>You violate these Terms</li>
                <li>Your use poses legal or security risks</li>
                <li>Required by law</li>
              </ul>
              <p>
                Termination does not limit our legal rights.
              </p>
              
              <h4>12. Disclaimers</h4>
              <p>
                <strong>THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE."</strong>
              </p>
              <p>
                WE DISCLAIM ALL WARRANTIES, EXPRESS OR IMPLIED, INCLUDING:
              </p>
              <ul>
                <li>MERCHANTABILITY</li>
                <li>FITNESS FOR A PARTICULAR PURPOSE</li>
                <li>NON-INFRINGEMENT</li>
              </ul>
              <p>
                <strong>USE OF THE SERVICE IS AT YOUR OWN RISK.</strong>
              </p>
              
              <h4>13. Limitation of Liability</h4>
              <p>
                TO THE MAXIMUM EXTENT PERMITTED BY LAW, CETARUS SHALL NOT BE LIABLE FOR:
              </p>
              <ul>
                <li>INDIRECT, INCIDENTAL, OR CONSEQUENTIAL DAMAGES</li>
                <li>HEALTH OUTCOMES OR DECISIONS</li>
                <li>DATA LOSS OR INTERRUPTION</li>
                <li>USER-GENERATED CONTENT OR THIRD-PARTY ACTIONS</li>
              </ul>
              
              <h4>14. Changes to the Terms</h4>
              <p>
                We may update these Terms from time to time.
              </p>
              <p>
                Continued use of the Service after changes means you accept the updated Terms.
              </p>
              
              <h4>15. Contact</h4>
              <p>
                If you have questions about these Terms, contact us at:
              </p>
              <p>
                <a href="mailto:support@cetarus.com">support@cetarus.com</a>
              </p>
            </div>
            <div className="modal-footer">
              <div className="modal-footer-content">
                {!hasScrolledToBottom && (
                  <p className="scroll-prompt">
                    Please scroll to the bottom to continue
                  </p>
                )}
                <button
                  className={`btn btn--md btn--rounded ${hasAgreed ? 'btn--success' : 'btn--primary'}`}
                  disabled={!hasScrolledToBottom}
                  onClick={() => {
                    setHasAgreed(true);
                    handleInputChange('agreeToTerms', true);
                    setTimeout(() => {
                      setShowTermsModal(false);
                    }, 500);
                  }}
                  style={{ 
                    opacity: hasScrolledToBottom ? 1 : 0.5,
                    cursor: hasScrolledToBottom ? 'pointer' : 'not-allowed'
                  }}
                >
                  {hasAgreed ? (
                    <>
                      I Agree <span className="agree-checkmark">✓</span>
                    </>
                  ) : (
                    'I Agree'
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SignUp;